import { useEffect, useState } from "react";
import {
  TextField, MenuItem, Button, Stack, Alert, CircularProgress, Typography, Box,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import {
  Receptor, ArcaLookup, lookupArca,
  CONDICION_IVA_OPTIONS, ACTIVIDAD_OPTIONS, PROVINCIAS_AR,
} from "../services/billing";

/**
 * Campos del receptor + consulta al padrón de ARCA, compartidos por el editor
 * de receptor (lápiz de la tabla) y el alta de factura manual. Viven acá y no
 * duplicados en cada diálogo a propósito: son el MISMO formulario, y cuando se
 * agregue un campo tiene que aparecer en los dos sin que nadie se acuerde.
 *
 * Controlado por el padre (`value`/`onChange`): el padre es el que arma el
 * payload al guardar. Lo único que el componente se guarda para sí son las
 * marcas de "esto lo trajo el padrón", que son presentación.
 */
export interface ReceptorFormValue {
  razonSocial: string;
  condId:      number | "";
  condDesc:    string;
  act:         number | "";
  email:       string;
  domicilio:   string;
  /** Jurisdicción para IIBB (nombre canónico de PROVINCIAS_AR o ""). */
  provincia:   string;
}

export const receptorFormFrom = (r?: Partial<Receptor> | null): ReceptorFormValue => ({
  razonSocial: r?.razon_social ?? "",
  condId:      r?.condicion_iva_id ?? "",
  condDesc:    r?.condicion_iva_desc ?? "",
  act:         r?.actividad ?? "",
  email:       r?.email ?? "",
  domicilio:   r?.domicilio ?? "",
  provincia:   r?.provincia ?? "",
});

// Espejo de email_sender.parse_recipients/_EMAIL_RE en el backend: el campo
// admite varios destinatarios separados por coma o punto y coma.
export const receptorEmails = (v: ReceptorFormValue): string[] =>
  v.email.split(/[,;]/).map((e) => e.trim().toLowerCase()).filter(Boolean);

export const receptorEmailsInvalidos = (v: ReceptorFormValue): string[] =>
  receptorEmails(v).filter((e) => !/^[^@\s,;]+@[^@\s,;]+\.[A-Za-z]{2,}$/.test(e));

/** Payload de `saveReceptor` a partir del formulario. */
export const receptorPayload = (v: ReceptorFormValue): Partial<Receptor> => ({
  razon_social:       v.razonSocial.trim(),
  condicion_iva_id:   v.condId === "" ? undefined : Number(v.condId),
  condicion_iva_desc: v.condDesc,
  actividad:          v.act === "" ? undefined : Number(v.act),
  email:              receptorEmails(v).join(", "),
  domicilio:          v.domicilio.trim(),
  provincia:          v.provincia,
});

interface Props {
  /** CUIT a consultar en el padrón. */
  cuit:      string;
  value:     ReceptorFormValue;
  onChange:  (v: ReceptorFormValue) => void;
  disabled?: boolean;
  /** Cambiar este valor reinicia las marcas de "vino del padrón". */
  resetKey?: unknown;
  /** El alta de factura lo usa para derivar el collector de la razón social. */
  onLookup?: (r: ArcaLookup) => void;
}

export default function ReceptorFields({
  cuit, value, onChange, disabled, resetKey, onLookup,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [dePadron, setDePadron] = useState<Record<string, boolean>>({});

  useEffect(() => { setDePadron({}); setErr(null); setInfo(null); }, [resetKey]);

  const cuitValido = /^\d{11}$/.test(cuit.replace(/\D/g, ""));
  const emailsInvalidos = receptorEmailsInvalidos(value);
  const emails = receptorEmails(value);

  const set = (patch: Partial<ReceptorFormValue>, dejaDeSerDePadron?: string) => {
    if (dejaDeSerDePadron) setDePadron((d) => ({ ...d, [dejaDeSerDePadron]: false }));
    onChange({ ...value, ...patch });
  };

  async function onConsultPadronArca() {
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await lookupArca(cuit.replace(/\D/g, ""));
      if (!r.razon_social) { setInfo("Sin datos encontrados"); return; }
      // El padrón devuelve razón social siempre, y condición IVA / domicilio
      // best-effort. Se aplica solo lo que vino: pisar con vacío borraría datos
      // cargados a mano que el padrón no expone.
      const next: ReceptorFormValue = { ...value, razonSocial: r.razon_social };
      const marcas: Record<string, boolean> = { razonSocial: true };
      const traidos = ["Razón Social"];
      if (r.condicion_iva_id) {
        next.condId = r.condicion_iva_id;
        next.condDesc = r.condicion_iva_desc
          ?? CONDICION_IVA_OPTIONS.find((o) => o.id === r.condicion_iva_id)?.desc ?? "";
        marcas.condId = true;
        traidos.push("Condición frente al IVA");
      }
      if (r.domicilio) {
        next.domicilio = r.domicilio;
        marcas.domicilio = true;
        traidos.push("Domicilio");
      }
      if (r.provincia) {
        next.provincia = r.provincia;
        marcas.provincia = true;
        traidos.push("Provincia");
      }
      onChange(next);
      setDePadron((d) => ({ ...d, ...marcas }));
      const faltantes = ["Condición frente al IVA", "Domicilio", "Provincia"].filter((c) => !traidos.includes(c));
      setInfo(`Del padrón de ARCA: ${traidos.join(", ")}.`
        + (faltantes.length ? ` No vino ${faltantes.join(" ni ")} — completalo a mano.` : ""));
      onLookup?.(r);
    } catch (e: any) {
      setErr(e.message ?? "No se pudo consultar el padrón de ARCA");
    } finally { setBusy(false); }
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Button
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} /> : <CloudSyncIcon />}
          onClick={onConsultPadronArca}
          disabled={busy || disabled || !cuitValido}
          sx={{ whiteSpace: "nowrap" }}
        >
          Consultar padrón ARCA
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {cuitValido
            ? "Trae Razón Social, Condición frente al IVA y Domicilio del padrón oficial de ARCA."
            : "Ingresá un CUIT de 11 dígitos para consultar el padrón."}
        </Typography>
      </Box>

      <TextField
        label="Razón Social"
        value={value.razonSocial}
        onChange={(e) => set({ razonSocial: e.target.value }, "razonSocial")}
        disabled={disabled}
        fullWidth
        placeholder="Ej: B2BBNPL S.A. / Juan Bosetti"
        InputProps={{ sx: dePadron.razonSocial ? { color: "#2e7d32", fontWeight: 600 } : undefined }}
        helperText={dePadron.razonSocial ? "Obtenido del padrón de ARCA" : undefined}
      />

      <TextField
        select
        label="Condición frente al IVA"
        value={value.condId}
        onChange={(e) => {
          const id = Number(e.target.value);
          set({ condId: id, condDesc: CONDICION_IVA_OPTIONS.find((o) => o.id === id)?.desc ?? "" }, "condId");
        }}
        disabled={disabled}
        fullWidth
        helperText={dePadron.condId ? "Obtenida del padrón de ARCA" : undefined}
      >
        {CONDICION_IVA_OPTIONS.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.id} · {o.desc}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({o.for_cmp})
            </Typography>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Actividad de facturación (actividad de B2BBNPL al emitir)"
        value={value.act}
        onChange={(e) => set({ act: Number(e.target.value) })}
        disabled={disabled}
        fullWidth
      >
        {ACTIVIDAD_OPTIONS.map((o) => (
          <MenuItem key={o.id} value={o.id}>{o.id} · {o.desc}</MenuItem>
        ))}
      </TextField>

      {/* type="text" y no "email": el input nativo de email rechaza las comas y
          acá se admiten varios destinatarios. */}
      <TextField
        label="Emails de facturación (destinatarios del PDF)"
        value={value.email}
        onChange={(e) => set({ email: e.target.value })}
        disabled={disabled}
        fullWidth
        placeholder="pagos@cliente.com, contaduria@cliente.com"
        error={emailsInvalidos.length > 0}
        helperText={
          emailsInvalidos.length
            ? `Direcciones inválidas: ${emailsInvalidos.join(", ")}`
            : emails.length > 1
              ? `${emails.length} destinatarios — todos reciben la factura`
              : "Podés poner varios separados por coma"
        }
      />

      <TextField
        label="Domicilio Comercial (aparece en el PDF de factura)"
        value={value.domicilio}
        onChange={(e) => set({ domicilio: e.target.value }, "domicilio")}
        disabled={disabled}
        fullWidth
        placeholder="Ej: Av. Corrientes 123 Piso 4, CABA"
        helperText={dePadron.domicilio ? "Obtenido del padrón de ARCA" : undefined}
      />

      {/* Jurisdicción para el informe de IIBB (CP-250). Si queda vacía, el
          informe la infiere del onboarding del colector o del domicilio. */}
      <TextField
        select
        label="Provincia (jurisdicción para Ingresos Brutos)"
        value={PROVINCIAS_AR.includes(value.provincia) ? value.provincia : ""}
        onChange={(e) => set({ provincia: e.target.value }, "provincia")}
        disabled={disabled}
        fullWidth
        helperText={dePadron.provincia
          ? "Obtenida del padrón de ARCA"
          : "Vacía = el informe por provincia la infiere del onboarding o del domicilio"}
      >
        <MenuItem value=""><em>Sin definir</em></MenuItem>
        {PROVINCIAS_AR.map((p) => (
          <MenuItem key={p} value={p}>{p}</MenuItem>
        ))}
      </TextField>

      {err  && <Alert severity="error">{err}</Alert>}
      {info && <Alert severity="success">{info}</Alert>}
    </Stack>
  );
}
